import type { SxProps, Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { useThemeId } from './ThemeContext';

/**
 * Токены 1:1 с макетом Figma (фрейм «мои боты», десктоп SMS).
 * Тёмная тема не использует эти константы — только isSmsLight.
 */
export const SMS_PAGE = {
  bg: '#fcfcfc',
  card: '#ffffff',
  row: '#e8eaec',
  shellDark: '#252839',
  shellSidebar: '#252839',
  shellYellow: '#FFB300',
  shellDivider: 'rgba(211, 212, 215, 0.1)',
  text: '#0d0d0d',
  muted: '#94a3b8',
  accent: '#FFB300',
  accentHover: '#e6a200',
  greenBot: '#31774f',
  errorRed: '#ff0004',
  triggerChipBg: '#c8c8c8',
  triggerChipText: '#000000',
  rowBorder: 'rgba(0,0,0,0.2)',
  shadow: '0px 6px 18px rgba(28, 127, 194, 0.12)',
  subtleShadow: '0px 1px 1px 0px rgba(0,0,0,0.14)',
  borderInput: 'rgba(13,13,13,0.23)',
  borderInputHover: 'rgba(13,13,13,0.35)',
  disabledBtnBg: 'rgba(13,13,13,0.12)',
  disabledBtnText: 'rgba(13,13,13,0.3)',
  outlineBorder: 'rgba(0,0,0,0.12)',
  divider: 'rgba(0,0,0,0.08)',
  white: '#ffffff',
  backButtonBg: 'rgba(255, 179, 0, 0.16)',
  backButtonBgHover: 'rgba(255, 179, 0, 0.28)',
  /** Заголовок H1 в макете */
  titlePx: { xs: '1.35rem', sm: '1.75rem', md: '28px' },
  titleWeight: 600,
  /** Подзаголовок под H1 */
  subtitleSize: '1rem',
  /** «Токен вашего бота» */
  sectionTitleSize: 16,
  sectionTitleWeight: 500,
  /** Подсказка под секцией */
  hintSize: 14,
  hintWeight: 500,
} as const;

/**
 * Палитра редактора сценариев из кадров Figma «светлая тема» / «темная тема».
 * Карточки блоков и узлы остаются светлыми в обеих темах.
 * Меняются оболочка, холст, тулбар и панель «Свойства».
 */
export const SMS_EDITOR = {
  light: {
    page: '#fcfcfc',
    panel: '#ffffff',
    canvas: '#fcfcfc',
    canvasDots: 'rgba(37,40,57,0.16)',
    blockCard: '#fff9eb',
    blockCardBorder: '#ffe8b3',
    input: '#f7f7f7',
    accent: '#ffb300',
    accentSoft: 'rgba(255,179,0,0.08)',
    text: '#252839',
    muted: '#858790',
    chipBorder: '#d1d1d1',
  },
  dark: {
    page: '#070a1f',
    panel: '#070a1f',
    panelRaised: '#313344',
    canvas: '#515361',
    canvasDots: 'rgba(255,255,255,0.28)',
    border: 'rgba(209,209,209,0.28)',
    borderStrong: '#ffb300',
    text: '#ffffff',
    muted: '#858790',
    accent: '#ffb300',
    accentHover: '#ffc233',
    onAccent: '#252839',
    blockCard: '#fff9eb',
    blockCardBorder: '#ffe8b3',
    chipBorder: '#d1d1d1',
  },
} as const;

/** Иконка корзины в конструкторе сцен (Figma: 16×18 px, #FF0004) */
export const flowDeleteIconSx: SxProps<Theme> = {
  width: 16,
  height: 18,
  fontSize: 16,
  color: SMS_PAGE.errorRed,
  flexShrink: 0,
};

export function useSmsPageDesign() {
  const theme = useTheme();
  const { themeId } = useThemeId();
  const isSmsLight = themeId === 'smsby' && theme.palette.mode === 'light';
  return { theme, themeId, isSmsLight };
}

export function smsPaperCardSx(theme: Theme, isSmsLight: boolean): SxProps<Theme> {
  if (isSmsLight) {
    return {
      bgcolor: SMS_PAGE.card,
      border: 'none',
      boxShadow: SMS_PAGE.shadow,
      borderRadius: '8px',
    };
  }
  return {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
  };
}

export function smsGridCardSx(theme: Theme, isSmsLight: boolean): SxProps<Theme> {
  if (isSmsLight) {
    return {
      bgcolor: SMS_PAGE.card,
      border: 'none',
      boxShadow: SMS_PAGE.shadow,
      borderRadius: '8px',
      '&:hover': {
        boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
      },
    };
  }
  return {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
    '&:hover': { borderColor: 'primary.main' },
  };
}

export function smsSearchFieldSx(useFigmaLayout: boolean, isDark = false): SxProps<Theme> | undefined {
  if (!useFigmaLayout) return undefined;
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '999px',
      minHeight: 40,
      bgcolor: isDark ? '#172033' : SMS_PAGE.white,
      color: isDark ? '#f8fafc' : SMS_PAGE.text,
      '& fieldset': {
        borderColor: isDark ? 'rgba(148, 163, 184, 0.22)' : SMS_PAGE.borderInput,
      },
      '&:hover fieldset': {
        borderColor: isDark ? 'rgba(148, 163, 184, 0.35)' : SMS_PAGE.borderInputHover,
      },
      '&.Mui-focused fieldset': {
        borderColor: isDark ? 'rgba(56, 189, 248, 0.5)' : SMS_PAGE.borderInputHover,
      },
    },
    '& .MuiOutlinedInput-input': {
      py: '9.5px',
      px: '14px',
      fontSize: 16,
      color: isDark ? '#f8fafc' : undefined,
      '&::placeholder': {
        color: isDark ? '#94a3b8' : '#858790',
        opacity: 1,
      },
    },
    '& .MuiInputAdornment-root': {
      ml: '6px',
      mr: '-2px',
      '& .MuiSvgIcon-root': {
        color: isDark ? '#94a3b8' : 'rgba(13, 13, 13, 0.54)',
      },
    },
  };
}

export function smsTokenFieldSx(useFigmaStyles: boolean, isDark = false): SxProps<Theme> | undefined {
  if (!useFigmaStyles) return undefined;
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '999px',
      minHeight: 40,
      height: 40,
      bgcolor: isDark ? '#172033' : undefined,
      color: isDark ? '#f8fafc' : undefined,
      '& fieldset': {
        borderColor: isDark ? 'rgba(148, 163, 184, 0.22)' : SMS_PAGE.borderInput,
      },
      '&:hover fieldset': {
        borderColor: isDark ? 'rgba(148, 163, 184, 0.35)' : SMS_PAGE.borderInputHover,
      },
      '&.Mui-focused fieldset': {
        borderColor: isDark ? 'rgba(56, 189, 248, 0.5)' : undefined,
      },
    },
    '& .MuiOutlinedInput-input': {
      py: '9.5px',
      px: '14px',
      fontSize: 16,
      color: isDark ? '#f8fafc' : undefined,
      '&::placeholder': {
        color: isDark ? '#94a3b8' : undefined,
        opacity: 1,
      },
    },
    '& .MuiFormHelperText-root': {
      color: isDark ? '#94a3b8' : undefined,
    },
  };
}

export function smsConnectButtonSx(useFigmaStyles: boolean, disabled: boolean, loading?: boolean): SxProps<Theme> {
  const base: SxProps<Theme> = {
    textTransform: 'none',
    borderRadius: '100px',
  };
  if (!useFigmaStyles) {
    return {
      textTransform: 'uppercase',
      borderRadius: '100px',
    };
  }
  return (theme) => {
    const isDark = theme.palette.mode === 'dark';
    if (loading) {
      return {
        ...base,
        bgcolor: isDark ? 'rgba(148, 163, 184, 0.28)' : '#B0BEC5',
        color: isDark ? '#f8fafc' : '#ffffff',
        boxShadow: 'none',
        '&:hover': { bgcolor: isDark ? 'rgba(148, 163, 184, 0.36)' : '#9EA7AD' },
        '&.Mui-disabled': {
          bgcolor: isDark ? 'rgba(148, 163, 184, 0.28)' : '#B0BEC5',
          color: isDark ? 'rgba(248, 250, 252, 0.7)' : 'rgba(255,255,255,0.7)',
        },
      };
    }
    if (!disabled) {
      if (isDark) {
        return {
          ...base,
          bgcolor: '#38bdf8',
          color: '#0f172a',
          boxShadow: 'none',
          '&:hover': { bgcolor: '#7dd3fc', boxShadow: 'none' },
        };
      }
      return {
        ...base,
        bgcolor: SMS_PAGE.shellYellow,
        color: SMS_PAGE.white,
        boxShadow: 'none',
        '&:hover': { bgcolor: SMS_PAGE.accentHover, boxShadow: 'none' },
      };
    }
    if (isDark) {
      return {
        ...base,
        bgcolor: 'rgba(148, 163, 184, 0.1)',
        color: 'rgba(148, 163, 184, 0.45)',
        border: '1px solid rgba(148, 163, 184, 0.22)',
        boxShadow: 'none',
        '&:hover': { bgcolor: 'rgba(148, 163, 184, 0.1)' },
        '&.Mui-disabled': {
          bgcolor: 'rgba(148, 163, 184, 0.1)',
          color: 'rgba(148, 163, 184, 0.45)',
          border: '1px solid rgba(148, 163, 184, 0.22)',
        },
      };
    }
    return {
      ...base,
      bgcolor: '#eaeaea',
      color: '#adadad',
      border: '1px solid #adadad',
      boxShadow: 'none',
      '&:hover': { bgcolor: '#eaeaea' },
      '&.Mui-disabled': {
        bgcolor: '#eaeaea',
        color: '#adadad',
        border: '1px solid #adadad',
      },
    };
  };
}

export function smsChipBotUsernameSx(): SxProps<Theme> {
  return {
    bgcolor: SMS_PAGE.greenBot,
    color: SMS_PAGE.white,
    fontWeight: SMS_PAGE.sectionTitleWeight,
    height: 37,
    borderRadius: '32px',
    '& .MuiChip-label': { px: 2.5 },
  };
}

export function smsChipWebhookActiveSx(): SxProps<Theme> {
  return {
    height: 37,
    borderRadius: '32px',
    borderColor: SMS_PAGE.greenBot,
    color: SMS_PAGE.greenBot,
    fontWeight: SMS_PAGE.sectionTitleWeight,
  };
}

export function smsChipBotStoppedSx(): SxProps<Theme> {
  return {
    bgcolor: SMS_PAGE.white,
    color: SMS_PAGE.errorRed,
    borderColor: SMS_PAGE.errorRed,
    height: 37,
    borderRadius: '32px',
    fontWeight: SMS_PAGE.sectionTitleWeight,
  };
}

export function smsStartBotButtonSx(): SxProps<Theme> {
  return {
    bgcolor: SMS_PAGE.accent,
    color: SMS_PAGE.white,
    border: `1px solid ${SMS_PAGE.accent}`,
    '&:hover': { bgcolor: SMS_PAGE.accentHover, borderColor: SMS_PAGE.accentHover },
  };
}

export function smsAssignScenarioButtonSx(): SxProps<Theme> {
  return {
    color: SMS_PAGE.accent,
    borderColor: SMS_PAGE.accent,
    '&:hover': {
      borderColor: SMS_PAGE.accentHover,
      bgcolor: 'rgba(255, 179, 0, 0.08)',
    },
  };
}

export function smsTriggerChipSx(theme: Theme, isDup: boolean): SxProps<Theme> {
  if (isDup) {
    return {
      bgcolor: 'rgba(255,255,255,0.9)',
      color: theme.palette.error.main,
      fontWeight: SMS_PAGE.sectionTitleWeight,
      height: 30,
      borderRadius: '81px',
      minWidth: 65,
      border: `1px solid ${theme.palette.error.main}`,
    };
  }
  return {
    bgcolor: SMS_PAGE.triggerChipBg,
    color: SMS_PAGE.triggerChipText,
    fontWeight: SMS_PAGE.sectionTitleWeight,
    height: 30,
    borderRadius: '81px',
    minWidth: 65,
  };
}

export function smsScenarioRowSx(theme: Theme, isSmsLight: boolean, isDup: boolean): SxProps<Theme> {
  if (!isSmsLight) {
    return {
      bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'),
      border: (t) =>
        `1px solid ${
          isDup
            ? t.palette.error.main
            : t.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.08)'
              : 'rgba(0,0,0,0.08)'
        }`,
    };
  }
  return {
    bgcolor: SMS_PAGE.row,
    border: `1px solid ${isDup ? theme.palette.error.main : SMS_PAGE.rowBorder}`,
  };
}

export function smsEditorToolbarSx(isSmsLight: boolean, isDark = false): SxProps<Theme> {
  if (isSmsLight) {
    return {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 2,
      py: 0.75,
      minHeight: 48,
      bgcolor: SMS_PAGE.bg,
      borderBottom: `1px solid ${SMS_PAGE.divider}`,
    };
  }
  if (!isDark) {
    return {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 2,
      py: 0.75,
      minHeight: 48,
      borderBottom: 1,
      borderColor: 'divider',
    };
  }
  return {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 2,
    py: 0.75,
    minHeight: 48,
    bgcolor: SMS_EDITOR.dark.page,
    color: SMS_EDITOR.dark.text,
    borderBottom: `1px solid ${SMS_EDITOR.dark.border}`,
    '& .MuiInput-root': {
      color: SMS_EDITOR.dark.text,
      '&:before': { borderBottomColor: SMS_EDITOR.dark.border },
      '&:hover:not(.Mui-disabled):before': { borderBottomColor: SMS_EDITOR.dark.muted },
      '&:after': { borderBottomColor: SMS_EDITOR.dark.accent },
    },
    '& .MuiIconButton-root': {
      color: SMS_EDITOR.dark.muted,
      '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: SMS_EDITOR.dark.text },
    },
  };
}

export const smsBackIconButtonProps = {
  sx: {
    width: 32,
    height: 32,
    p: '6px',
    bgcolor: 'transparent',
    color: 'primary.main',
    borderRadius: '6px',
    '&:hover': { bgcolor: SMS_PAGE.backButtonBg },
  },
} as const;

/** Тулбар над холстом: перенос строк, без горизонтального «убегания» за край (от ~1440px и ниже). */
export function smsFlowToolbarPanelSx(isSmsLight: boolean, isDark = false): SxProps<Theme> {
  const base = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    alignContent: 'flex-start',
    gap: 1,
    px: { xs: 1, sm: 1.5, md: 2 },
    py: 1,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    rowGap: 1,
  } as const;
  if (isSmsLight) {
    return {
      ...base,
      borderRadius: '8px',
      bgcolor: 'transparent',
      border: 'none',
      boxShadow: 'none',
      color: SMS_EDITOR.light.text,
      '& .MuiInputLabel-root': { color: SMS_EDITOR.light.muted },
      '& .MuiOutlinedInput-root': {
        bgcolor: SMS_PAGE.white,
        borderRadius: '8px',
        '& fieldset': { borderColor: '#d9d9d9' },
        '&:hover fieldset': { borderColor: SMS_EDITOR.light.accent },
        '&.Mui-focused fieldset': { borderColor: SMS_EDITOR.light.accent },
      },
    };
  }
  if (!isDark) {
    return {
      ...base,
      borderRadius: 1,
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: 1,
    };
  }
  return {
    ...base,
    borderRadius: '8px',
    bgcolor: SMS_EDITOR.dark.page,
    border: 'none',
    boxShadow: 'none',
    color: SMS_EDITOR.dark.text,
    '& .MuiInputLabel-root': { color: SMS_EDITOR.dark.muted },
    '& .MuiInputBase-root': {
      bgcolor: SMS_EDITOR.dark.panelRaised,
      color: SMS_EDITOR.dark.text,
      '& fieldset': { borderColor: SMS_EDITOR.dark.border },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.28)' },
      '&.Mui-focused fieldset': { borderColor: SMS_EDITOR.dark.accent },
    },
    '& .MuiSvgIcon-root': { color: 'inherit' },
  };
}

/** Кнопки тулбара холста: в светлой теме мягкая заливка или жёлтый контур, в тёмной — сплошной жёлтый. */
export function smsFlowToolbarActionSx(
  isSmsLight: boolean,
  isDark: boolean,
  tone: 'soft' | 'outline' = 'outline',
): SxProps<Theme> | undefined {
  if (!isSmsLight && !isDark) return undefined;
  const base = {
    borderRadius: '100px',
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: 13,
    minHeight: 37,
    px: 2,
    boxShadow: 'none',
    '& .MuiButton-startIcon': { marginLeft: 0, marginRight: '5px' },
  };
  if (isDark) {
    return {
      ...base,
      bgcolor: SMS_EDITOR.dark.accent,
      color: SMS_EDITOR.dark.onAccent,
      border: `1px solid ${SMS_EDITOR.dark.accent}`,
      '&:hover': { bgcolor: SMS_EDITOR.dark.accentHover, borderColor: SMS_EDITOR.dark.accentHover, boxShadow: 'none' },
      '&.Mui-disabled': {
        bgcolor: 'rgba(255,179,0,0.35)',
        color: 'rgba(37,40,57,0.45)',
        borderColor: 'transparent',
      },
    };
  }
  if (tone === 'soft') {
    return {
      ...base,
      bgcolor: SMS_EDITOR.light.accentSoft,
      color: SMS_EDITOR.light.text,
      border: '1px solid transparent',
      '&:hover': { bgcolor: 'rgba(255,179,0,0.16)', boxShadow: 'none' },
      '&.Mui-disabled': { bgcolor: 'rgba(255,179,0,0.04)', color: 'rgba(37,40,57,0.35)' },
    };
  }
  return {
    ...base,
    bgcolor: '#ffffff',
    color: SMS_EDITOR.light.text,
    border: `1px solid ${SMS_EDITOR.light.accent}`,
    '&:hover': { bgcolor: SMS_EDITOR.light.accentSoft, borderColor: SMS_EDITOR.light.accent, boxShadow: 'none' },
    '&.Mui-disabled': { color: 'rgba(37,40,57,0.35)', borderColor: 'rgba(255,179,0,0.35)' },
  };
}

export function smsDialogPaperSx(isSmsLight: boolean, extra?: SxProps<Theme>): SxProps<Theme> {
  const base: SxProps<Theme> = isSmsLight
    ? {
        bgcolor: SMS_PAGE.white,
        backgroundImage: 'none',
        borderRadius: '8px',
        border: `1px solid ${SMS_PAGE.outlineBorder}`,
        boxShadow: SMS_PAGE.shadow,
      }
    : {
        backgroundColor: 'background.paper',
        backgroundImage: 'none',
        border: '1px solid',
        borderColor: 'divider',
      };
  return extra ? ([base, extra] as SxProps<Theme>) : base;
}

export function smsFlowCanvasChromeSx(isSmsLight: boolean, isDark = false): SxProps<Theme> {
  if (isSmsLight) {
    return {
      borderRadius: 0,
      overflow: 'hidden',
      border: 'none',
      bgcolor: SMS_EDITOR.light.canvas,
      flex: 1,
      '& .react-flow': { bgcolor: SMS_EDITOR.light.canvas },
    };
  }
  if (!isDark) {
    return {
      borderRadius: 1,
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'divider',
      flex: 1,
    };
  }
  return {
    borderRadius: 0,
    overflow: 'hidden',
    border: `1px solid ${SMS_EDITOR.dark.border}`,
    bgcolor: SMS_EDITOR.dark.canvas,
    flex: 1,
    '& .react-flow': { bgcolor: SMS_EDITOR.dark.canvas },
    '& .react-flow__controls': {
      overflow: 'hidden',
      border: `1px solid ${SMS_EDITOR.dark.border}`,
      borderRadius: '8px',
      boxShadow: '0 6px 18px rgba(0,0,0,0.28)',
    },
    '& .react-flow__controls-button': {
      bgcolor: SMS_EDITOR.dark.panelRaised,
      borderBottomColor: SMS_EDITOR.dark.border,
      fill: SMS_EDITOR.dark.text,
      '&:hover': { bgcolor: 'rgba(255,179,0,0.16)' },
      '& svg': { fill: SMS_EDITOR.dark.text },
    },
  };
}

export function smsFlowLeftColumnSx(isSmsLight: boolean, isDark = false): SxProps<Theme> {
  if (isSmsLight) {
    return {
      borderRight: `1px solid ${SMS_PAGE.divider}`,
      bgcolor: SMS_PAGE.card,
    };
  }
  if (!isDark) {
    return {
      borderRight: '1px solid',
      borderColor: 'divider',
    };
  }
  return {
    borderRight: `1px solid ${SMS_EDITOR.dark.border}`,
    bgcolor: SMS_EDITOR.dark.panel,
    color: SMS_EDITOR.dark.text,
  };
}

export function smsFloatingSaveBarSx(isSmsLight: boolean, isDark = false): SxProps<Theme> {
  const figmaSaveButtons = {
    '& .MuiButton-root': {
      cursor: 'pointer',
      borderRadius: '100px',
      textTransform: 'none',
      fontWeight: 500,
      minWidth: 125,
      minHeight: 37,
      px: 2,
      boxShadow: 'none',
    },
    '& .MuiButton-outlined': {
      bgcolor: '#ffffff',
      color: '#252839',
      borderColor: '#ffb300',
      '&:hover': { bgcolor: 'rgba(255,179,0,0.08)', borderColor: '#ffb300' },
    },
    '& .MuiButton-contained': {
      bgcolor: '#ffb300',
      color: '#ffffff',
      border: '1px solid #ffb300',
      '&:hover': { bgcolor: '#e6a200' },
    },
  } as const;
  if (isSmsLight || isDark) {
    return {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '9px',
      px: 0,
      py: 0,
      bgcolor: 'transparent',
      ...figmaSaveButtons,
    };
  }
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    px: 2,
    py: 1,
    borderRadius: 3,
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: 2,
    '& .MuiButton-root': { cursor: 'pointer' },
  };
}

/** Тонкий скроллбар 6px — цвета из макета SMS.by (#858790 светлая, #94a3b8 тёмная). */
export const SMS_SCROLLBAR_SIZE = 6;

export const SMS_SCROLLBAR_COLORS = {
  light: {
    thumb: 'rgba(133, 135, 144, 0.45)',
    thumbHover: 'rgba(133, 135, 144, 0.65)',
  },
  dark: {
    thumb: 'rgba(148, 163, 184, 0.28)',
    thumbHover: 'rgba(148, 163, 184, 0.42)',
  },
} as const;

export function getSmsScrollbarColors(isDark: boolean) {
  return isDark ? SMS_SCROLLBAR_COLORS.dark : SMS_SCROLLBAR_COLORS.light;
}

export function smsPageScrollbarGlobalStyles(isDark: boolean) {
  const { thumb, thumbHover } = getSmsScrollbarColors(isDark);
  return {
    html: {
      scrollbarWidth: 'thin',
      scrollbarColor: `${thumb} transparent`,
    },
    body: {
      scrollbarWidth: 'thin',
      scrollbarColor: `${thumb} transparent`,
    },
    'html::-webkit-scrollbar, body::-webkit-scrollbar': {
      width: SMS_SCROLLBAR_SIZE,
      height: SMS_SCROLLBAR_SIZE,
    },
    'html::-webkit-scrollbar-track, body::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    'html::-webkit-scrollbar-thumb, body::-webkit-scrollbar-thumb': {
      backgroundColor: thumb,
      borderRadius: 999,
    },
    'html::-webkit-scrollbar-thumb:hover, body::-webkit-scrollbar-thumb:hover': {
      backgroundColor: thumbHover,
    },
  };
}

/** Скроллбар для вложенных прокручиваемых блоков (sidebar, списки). */
export function smsScrollbarSx(isDark: boolean): SxProps<Theme> {
  const { thumb, thumbHover } = getSmsScrollbarColors(isDark);
  return {
    scrollbarWidth: 'thin',
    scrollbarColor: `${thumb} transparent`,
    '&::-webkit-scrollbar': {
      width: SMS_SCROLLBAR_SIZE,
      height: SMS_SCROLLBAR_SIZE,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: thumb,
      borderRadius: 999,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: thumbHover,
    },
  };
}

/** @deprecated Используйте {@link smsScrollbarSx}(isDark). */
export function smsDarkScrollbarSx(thumbColor = SMS_SCROLLBAR_COLORS.dark.thumb): SxProps<Theme> {
  const thumbHover = SMS_SCROLLBAR_COLORS.dark.thumbHover;
  return {
    scrollbarWidth: 'thin',
    scrollbarColor: `${thumbColor} transparent`,
    '&::-webkit-scrollbar': {
      width: SMS_SCROLLBAR_SIZE,
      height: SMS_SCROLLBAR_SIZE,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: thumbColor,
      borderRadius: 999,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: thumbHover,
    },
  };
}

export function smsGridTriggerChipSx(): SxProps<Theme> {
  return {
    bgcolor: SMS_PAGE.triggerChipBg,
    color: SMS_PAGE.triggerChipText,
    fontWeight: SMS_PAGE.sectionTitleWeight,
  };
}

/** Карточка шага тура подсказок (Figma: 28px, жёлтая тень). */
export function smsTourPaperSx(isSmsLight: boolean, isDark: boolean): SxProps<Theme> {
  if (isSmsLight) {
    return {
      bgcolor: SMS_PAGE.white,
      backgroundImage: 'none',
      borderRadius: '28px',
      border: 'none',
      boxShadow: '5px 8px 10px -1px rgba(255,179,0,0.12)',
      fontFamily: '"Montserrat", sans-serif',
    };
  }
  if (isDark) {
    return {
      bgcolor: '#1e293b',
      backgroundImage: 'none',
      borderRadius: '18px',
      border: '1px solid rgba(148, 163, 184, 0.22)',
      boxShadow: '0 14px 32px rgba(0, 0, 0, 0.38)',
    };
  }
  return {
    backgroundColor: 'background.paper',
    backgroundImage: 'none',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
  };
}

export function smsTourSpotlightOutlineColor(isSmsLight: boolean, isDark: boolean): string {
  if (isSmsLight) return SMS_PAGE.shellYellow;
  if (isDark) return '#38bdf8';
  return '#1976d2';
}

export function smsTourSpotlightRadius(isSmsLight: boolean): string {
  return isSmsLight ? '18px' : '8px';
}

export function smsTourPartLabelSx(isSmsLight: boolean, isDark: boolean): SxProps<Theme> {
  const color = isSmsLight ? SMS_PAGE.shellYellow : isDark ? '#38bdf8' : 'primary.main';
  return {
    display: 'block',
    fontWeight: 600,
    mb: 0.5,
    fontSize: 12,
    lineHeight: '16px',
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    color,
    fontFamily: isSmsLight ? '"Montserrat", sans-serif' : undefined,
  };
}

export function smsTourTitleSx(isSmsLight: boolean): SxProps<Theme> {
  return {
    fontSize: isSmsLight ? '1.125rem' : '1.1rem',
    fontWeight: isSmsLight ? 600 : undefined,
    lineHeight: isSmsLight ? '28px' : undefined,
    color: isSmsLight ? SMS_PAGE.shellDark : undefined,
    fontFamily: isSmsLight ? '"Montserrat", sans-serif' : undefined,
    pr: 0,
  };
}

export function smsTourBodySx(isSmsLight: boolean, isDark: boolean): SxProps<Theme> {
  return {
    whiteSpace: 'pre-line',
    fontSize: isSmsLight ? 14 : undefined,
    lineHeight: isSmsLight ? '22px' : undefined,
    color: isSmsLight ? '#858790' : isDark ? '#94a3b8' : undefined,
    fontFamily: isSmsLight ? '"Montserrat", sans-serif' : undefined,
  };
}

export function smsTourProgressSx(isSmsLight: boolean, isDark: boolean): SxProps<Theme> {
  return {
    display: 'block',
    mt: 0.5,
    fontSize: 11,
    lineHeight: '16px',
    color: isSmsLight ? '#b0b4bd' : isDark ? 'rgba(148,163,184,0.7)' : 'text.disabled',
    fontFamily: isSmsLight ? '"Montserrat", sans-serif' : undefined,
  };
}

export function smsTourSecondaryButtonSx(isSmsLight: boolean, isDark: boolean): SxProps<Theme> {
  if (isSmsLight) {
    return {
      borderRadius: '42px',
      bgcolor: 'rgba(255,179,0,0.08)',
      borderColor: SMS_PAGE.shellYellow,
      color: SMS_PAGE.shellYellow,
      fontSize: 13,
      fontWeight: 500,
      textTransform: 'none',
      fontFamily: '"Montserrat", sans-serif',
      minHeight: 36,
      '&:hover': {
        bgcolor: 'rgba(255,179,0,0.12)',
        borderColor: SMS_PAGE.shellYellow,
      },
      '&.Mui-disabled': {
        bgcolor: 'rgba(255,179,0,0.04)',
        borderColor: 'rgba(255,179,0,0.35)',
        color: 'rgba(255,179,0,0.45)',
      },
    };
  }
  if (isDark) {
    return {
      borderRadius: '42px',
      textTransform: 'none',
      minHeight: 36,
      borderColor: 'rgba(148,163,184,0.35)',
      color: '#e2e8f0',
      '&:hover': { borderColor: 'rgba(148,163,184,0.55)', bgcolor: 'rgba(148,163,184,0.08)' },
    };
  }
  return { textTransform: 'none', minHeight: 36 };
}

export function smsTourPrimaryButtonSx(isSmsLight: boolean, isDark: boolean): SxProps<Theme> {
  if (isSmsLight) {
    return {
      borderRadius: '42px',
      bgcolor: SMS_PAGE.shellYellow,
      color: SMS_PAGE.shellDark,
      fontSize: 13,
      fontWeight: 600,
      textTransform: 'none',
      fontFamily: '"Montserrat", sans-serif',
      minHeight: 36,
      boxShadow: 'none',
      '&:hover': { bgcolor: SMS_PAGE.accentHover, boxShadow: 'none' },
    };
  }
  if (isDark) {
    return {
      borderRadius: '42px',
      textTransform: 'none',
      minHeight: 36,
      fontWeight: 600,
      bgcolor: '#38bdf8',
      color: '#0f172a',
      boxShadow: 'none',
      '&:hover': { bgcolor: '#7dd3fc', boxShadow: 'none' },
    };
  }
  return { textTransform: 'none', minHeight: 36 };
}
